import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AI_MAX_OUTPUT_TOKENS, REDENEERNIVEAUS, TAALMODELLEN } from '@fleet/shared';
import { Alert, Badge, Button, Input, Tabs } from '../../design-system';
import type { TabItem } from '../../design-system';
import { Dropdown } from '../../components/ui/Dropdown';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { ArrowLeftIcon } from '../../components/ui/Icons';
import { Panel } from '../../components/ui/Panel';
import { ApiError } from '../../lib/apiClient';
import { useAuth } from '../auth/useAuth';
import { AccountsPanel } from './AccountsPanel';
import { testAi, useSettings, useUpdateSettings } from './settings.api';

const TABS: TabItem[] = [
  { id: 'integraties', label: 'Integraties & voorkeuren' },
  { id: 'accounts', label: 'Accounts' },
];

/** Gelijk aan `.main` in Layout.module.css: elk scherm in de app zit in
 *  dezelfde kolom, en instellingen hoort daar niet van af te wijken. */
const BREEDTE = 1280;

/** De modellen komen uit `shared`, zodat het scherm en de Worker het over
 *  dezelfde lijst hebben; een vrij tekstveld zou een typefout pas halverwege
 *  een upload laten stuklopen. */
const MODEL_OPTIES = TAALMODELLEN.map((m) => ({ value: m.id, label: m.naam }));
const NIVEAU_OPTIES = REDENEERNIVEAUS.map((n) => ({ value: n.id, label: n.naam }));

export function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const { data } = useSettings();
  const update = useUpdateSettings();

  const [aiModel, setAiModel] = useState('');
  const [aiRedeneerniveau, setAiRedeneerniveau] = useState('');
  const [aiMaxOutputTokens, setAiMaxOutputTokens] = useState<number>(
    AI_MAX_OUTPUT_TOKENS.standaard,
  );
  const [rdwAppToken, setRdwToken] = useState('');
  const [apkReminderDays, setApkDays] = useState(50);
  const [wasBudgetPerJaar, setWasBudget] = useState(0);
  const [administratieEmail, setAdminEmail] = useState('');
  const [tab, setTab] = useState('integraties');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [testMsg, setTestMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!data) return;
    setAiModel(data.aiModel);
    setAiRedeneerniveau(data.aiRedeneerniveau);
    setAiMaxOutputTokens(data.aiMaxOutputTokens);
    setApkDays(data.apkReminderDays);
    setWasBudget(data.wasBudgetPerJaar);
    setAdminEmail(data.administratieEmail);
  }, [data]);

  /** Persist the current form. Secret fields are only sent when (re)typed. */
  async function persist() {
    // Het plafond binnen de grenzen trekken voordat het weggaat: de server
    // weigert een waarde erbuiten, en dan zou ook de rest van het formulier
    // niet opgeslagen worden om één getypt getal.
    const plafond = Math.min(
      AI_MAX_OUTPUT_TOKENS.max,
      Math.max(AI_MAX_OUTPUT_TOKENS.min, Math.round(aiMaxOutputTokens) || 0),
    );
    setAiMaxOutputTokens(plafond);
    await update.mutateAsync({
      aiModel,
      aiRedeneerniveau,
      aiMaxOutputTokens: plafond,
      apkReminderDays,
      wasBudgetPerJaar,
      administratieEmail,
      ...(rdwAppToken ? { rdwAppToken } : {}),
    });
    setRdwToken('');
  }

  async function onSave() {
    setError('');
    setSaved(false);
    try {
      await persist();
      setSaved(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Opslaan mislukt');
    }
  }

  // Eerst opslaan, dan testen: anders test de server nog met het vorige model
  // terwijl het scherm het net gekozen model laat zien.
  async function onTest() {
    setTesting(true);
    setTestMsg(null);
    setError('');
    try {
      await persist();
      const r = await testAi();
      setTestMsg({ ok: true, text: `Verbinding gelukt (${r.model})` });
    } catch (e) {
      setTestMsg({ ok: false, text: e instanceof ApiError ? e.message : 'Verbinding mislukt' });
    } finally {
      setTesting(false);
    }
  }

  const accountsTab = tab === 'accounts';

  return (
    <div style={{ minHeight: '100vh' }}>
      {/* Instellingen staat buiten `Layout`, dus zonder het hoofdmenu. Je stapt
          hier even uit de navigatie; deze balk brengt je terug. Zelfde opzet als
          de balk boven de auto-detailpagina. */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: 'rgba(251,245,238,0.92)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid #E3D9CC',
        }}
      >
        {/* Zelfde 1280-container als de balk en de inhoud hieronder, zodat de
            terugknop op één lijn staat met het logo op de andere schermen. */}
        <div
          style={{
            maxWidth: BREEDTE,
            margin: '0 auto',
            padding: '14px 32px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            <ArrowLeftIcon />
            <span>
              Terug<span className="vs-vanaf-sm"> naar dashboard</span>
            </span>
          </Button>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          maxWidth: BREEDTE,
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
          padding: '28px 32px 64px',
        }}
      >
        <div>
          <Eyebrow>Instellingen</Eyebrow>
          <h2 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 900, color: '#111111' }}>
            {accountsTab ? 'Accounts & toegang.' : 'Integraties & voorkeuren.'}
          </h2>
        </div>

        <Tabs items={TABS} value={tab} onChange={setTab} />

        {accountsTab && <AccountsPanel />}

        {!accountsTab && !isAdmin && (
          <Alert tone="warning" title="Alleen-lezen">
            Alleen beheerders kunnen instellingen wijzigen.
          </Alert>
        )}

        {!accountsTab && (
          <>
            {/* Er staat hier geen sleutel meer: die zit als BYOK-credential in de
                Cloudflare AI Gateway. Wat een beheerder hier nog kiest is het
                model; of er überhaupt een gateway is, hangt aan de omgeving. */}
            <Panel style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111111' }}>
                AI (onderhoudsfacturen)
              </div>
              {/* Pas tonen als de instellingen binnen zijn: anders staat er tijdens
                  het laden even "niet ingesteld", en dat is precies het bericht dat
                  je niet ten onrechte wilt geven. */}
              {data && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div>
                    {data.aiBeschikbaar ? (
                      <Badge tone="success">Gateway verbonden</Badge>
                    ) : (
                      <Badge tone="warning">Niet ingesteld op deze omgeving</Badge>
                    )}
                  </div>
                  {!data.aiBeschikbaar && (
                    <span style={{ fontSize: 12, color: '#888888' }}>
                      De koppeling met de Cloudflare AI Gateway is configuratie van de Worker
                      (CF_AIG_TOKEN), niet iets wat je hier zet.
                    </span>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#444444' }}>Model</span>
                <Dropdown
                  label={undefined}
                  block
                  disabled={!isAdmin}
                  value={aiModel}
                  onChange={setAiModel}
                  options={MODEL_OPTIES}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#444444' }}>
                  Redeneerniveau
                </span>
                <Dropdown
                  label={undefined}
                  block
                  disabled={!isAdmin}
                  value={aiRedeneerniveau}
                  onChange={setAiRedeneerniveau}
                  options={NIVEAU_OPTIES}
                />
              </div>
              <Input
                label="Tokenplafond per factuur"
                type="number"
                min={String(AI_MAX_OUTPUT_TOKENS.min)}
                max={String(AI_MAX_OUTPUT_TOKENS.max)}
                step="500"
                value={String(aiMaxOutputTokens)}
                onChange={(e) => setAiMaxOutputTokens(Number(e.target.value) || 0)}
                hint={`Tussen ${AI_MAX_OUTPUT_TOKENS.min.toLocaleString('nl-NL')} en ${AI_MAX_OUTPUT_TOKENS.max.toLocaleString('nl-NL')}.`}
                disabled={!isAdmin}
              />
              <div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onTest}
                  disabled={testing || !isAdmin || data?.aiBeschikbaar === false}
                >
                  {testing ? 'Testen…' : 'Verbinding testen'}
                </Button>
                {testMsg && (
                  <span
                    style={{
                      marginLeft: 12,
                      fontSize: 13,
                      fontWeight: 700,
                      color: testMsg.ok ? '#3E7D63' : '#B0543B',
                    }}
                  >
                    {testMsg.text}
                  </span>
                )}
                <div style={{ fontSize: 12, color: '#888888', marginTop: 6 }}>
                  Testen slaat de ingevulde gegevens eerst op.
                </div>
              </div>
            </Panel>

            <Panel style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111111' }}>RDW &amp; APK</div>
              <Input
                label="RDW app-token (optioneel)"
                type="password"
                value={rdwAppToken}
                onChange={(e) => setRdwToken(e.target.value)}
                placeholder={data?.rdwAppTokenSet ? 'Ingesteld' : 'Verhoogt de RDW rate-limit'}
                hint="Laat leeg om de bestaande token te behouden."
                disabled={!isAdmin}
              />
              <Input
                label="APK-herinnering (dagen vooraf)"
                type="number"
                value={String(apkReminderDays)}
                onChange={(e) => setApkDays(Number(e.target.value) || 0)}
                disabled={!isAdmin}
              />
            </Panel>

            <Panel style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#111111' }}>Wagenparkbeleid</div>
              <Input
                label="Wasbudget per persoon per jaar (€)"
                type="number"
                min="0"
                step="5"
                value={String(wasBudgetPerJaar)}
                onChange={(e) => setWasBudget(Math.max(0, Number(e.target.value) || 0))}
                hint="Excl. btw, per kalenderjaar. Nul zet het wasbudget uit: dan verdwijnen het dashboardblok en de melding."
                disabled={!isAdmin}
              />
              <Input
                label="E-mailadres administratie"
                type="email"
                value={administratieEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                hint="Ontvanger van de mail 'nieuw kenteken' uit de checklist van een nieuwe auto. Leeg laten mag: dan opent de mail zonder geadresseerde."
                disabled={!isAdmin}
              />
            </Panel>

            {error && <Alert tone="danger">{error}</Alert>}
            {saved && <Alert tone="success">Instellingen opgeslagen.</Alert>}

            {isAdmin && (
              <div>
                <Button variant="primary" onClick={onSave} disabled={update.isPending}>
                  {update.isPending ? 'Opslaan…' : 'Opslaan'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
